import React, { useState, useEffect } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { fetchSellers, createSeller } from '../../services/user';
import toast from 'react-hot-toast';

const Manufacturers = () => {
    const [sellers, setSellers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalSellers, setTotalSellers] = useState(0);
    
    // Filters
    const [district, setDistrict] = useState('');
    const [category, setCategory] = useState('');
    const [availableDistricts, setAvailableDistricts] = useState([]);
    const [availableCategories, setAvailableCategories] = useState([]);
    
    const [search, setSearch] = useState('');
    
    // Form state
    const [formData, setFormData] = useState({ name: '', email: '', businessName: '', password: '' });

    const organisationId = localStorage.getItem('ud-user') || '';

    const loadSellers = async () => {
        setIsLoading(true);
        try {
            const data = await fetchSellers(organisationId, page, district, category, search);
            setSellers(data.sellers || []);
            setTotalPages(data.totalPages || 1);
            setTotalSellers(data.total || 0);
            setAvailableDistricts(data.availableDistricts || []);
            setAvailableCategories(data.availableCategories || []);
        } catch(err) {
            toast.error("Failed to load manufacturers");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSellers();
    }, [page, district, category, search]);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await createSeller({ ...formData, organisationId });
            toast.success("Seller created successfully!");
            setIsModalOpen(false);
            setFormData({ name: '', email: '', businessName: '', password: '' });
            loadSellers();
        } catch(err: any) {
            toast.error(err.response?.data?.error || "Failed to create seller");
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Manufacturer Management</h1>
                    <p className="text-gray-600">Manage all manufacturers under your organisation</p>
                </div>
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add Manufacturer
                </button>
            </div>

            <div className="mb-4 flex flex-col sm:flex-row sm:space-x-4 space-y-3 sm:space-y-0">
                <input
                    type="text"
                    placeholder="Search name, phone, email..."
                    className="border border-gray-300 rounded-md p-2 flex-1 max-w-sm"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
                <select
                    className="border border-gray-300 rounded-md p-2"
                    value={district}
                    onChange={(e) => { setDistrict(e.target.value); setPage(1); }}
                >
                    <option value="">All Districts</option>
                    {availableDistricts.map((d: string) => (
                        <option key={d} value={d}>{d}</option>
                    ))}
                </select>
                
                <select
                    className="border border-gray-300 rounded-md p-2"
                    value={category}
                    onChange={(e) => { setCategory(e.target.value); setPage(1); }}
                >
                    <option value="">All Categories</option>
                    {availableCategories.map((c: string) => (
                        <option key={c} value={c}>{c}</option>
                    ))}
                </select>
            </div>

            <div className="bg-white shadow rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manufacture Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {isLoading ? (
                            <tr><td colSpan={5} className="px-6 py-4 text-center text-gray-500">Loading sellers...</td></tr>
                        ) : sellers.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-4 text-center text-gray-500">No sellers found. Create one to get started.</td></tr>
                        ) : (
                            sellers.map((s: any) => {
                                const fullAddress = [s.building, s.street, s.locality, s.city, s.state, s.area_code].filter(Boolean).join(', ') || 'N/A';
                                return (
                                <tr key={s._id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{s.businessName || 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {s.supportCategory && s.supportCategory.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {s.supportCategory.map((c: string) => (
                                                    <span key={c} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                        {c}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">None</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div>{s.email}</div>
                                        <div className="text-gray-400">{s.phoneNumber || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <div className="font-semibold text-green-600">₹{s.totalRevenue?.toFixed(0) || 0}</div>
                                        <div className="text-xs text-gray-400">{s.totalOrders || 0} Orders | {s.totalProducts || 0} Products</div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={fullAddress}>
                                        {fullAddress}
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 shadow rounded-b-lg mt-2">
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Showing <span className="font-medium">{(page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(page * 10, totalSellers)}</span> of <span className="font-medium">{totalSellers}</span> results
                            </p>
                        </div>
                        <div>
                            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                >
                                    <span className="sr-only">Previous</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                                <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0">
                                    Page {page} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50"
                                >
                                    <span className="sr-only">Next</span>
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </nav>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Manufacturer Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Add New Manufacturer</h2>
                        <form onSubmit={handleCreate}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Business Name</label>
                                    <input required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.businessName} onChange={e => setFormData({...formData, businessName: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Contact Person Name</label>
                                    <input required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email Address (Login)</label>
                                    <input required type="email" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Temporary Password</label>
                                    <input required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">Create</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Manufacturers;
